import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  inject,
  DestroyRef,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faPlus, faEdit, faTimes } from "@fortawesome/free-solid-svg-icons";
import { catchError } from "rxjs/operators";
import { of } from "rxjs";

import { ExperienceDay } from "../../../../api/models/experience-day";
import { ExperienceDayStatus } from "../../../../api/models/experience-day-status";
import { Company } from "../../../../api/models/company";
import { CompanyService } from "../../../../api/services/company.service";
import { ModalService } from "../../../../shared/services/modal.service";
import {
  AlertService,
  alertMessages,
} from "../../../../shared/services/alert.service";
import { ModalCompanyProfileExperiencedayComponent } from "../../../../company/company-profile/modal-company-profile-experienceday/modal-company-profile-experienceday.component";

/**
 * Standalone component for managing company experience days table.
 * Handles CRUD operations via modals.
 *
 * Usage:
 * ```html
 * <app-experience-days-table
 *   [experienceDays]="experienceDays()"
 *   [company]="entity()"
 *   [loading]="experienceDaysLoading()"
 *   (refresh)="loadExperienceDays()"
 * ></app-experience-days-table>
 * ```
 */
@Component({
  selector: "app-experience-days-table",
  standalone: true,
  imports: [CommonModule, TranslateModule, FontAwesomeModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <h3 class="tab-title">{{ "Follow-up days" | translate }}</h3>

    <div class="card my-experiencedays">
      <div class="card-header">
        {{ "Follow-up days" | translate }}
      </div>
      <div class="card-body">
        <div class="btn-toolbar mb-3">
          <button
            type="button"
            class="btn btn-primary ml-auto mr-0"
            (click)="addExperienceDay()"
          >
            <fa-icon [icon]="icons.plus"></fa-icon>
            {{ "Add new" | translate }}
          </button>
        </div>

        @if (experienceDays.length > 0) {
          <div>
            <table class="table table-striped">
              <thead>
                <tr>
                  <th>{{ "Title" | translate }}</th>
                  <th>{{ "Status" | translate }}</th>
                  <th>{{ "Amount of registrations" | translate }}</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                @for (experienceDay of experienceDays; track experienceDay.id) {
                  <tr>
                    <td>
                      <h5 class="title">{{ experienceDay.title }}</h5>
                    </td>
                    <td>
                      {{ getStatusName(experienceDay.status) | translate }}
                    </td>
                    <td>
                      {{ experienceDay.numberOfRegistrations }}
                    </td>
                    <td>
                      <button
                        class="btn btn-secondary btn-block btn-sm"
                        (click)="editExperienceDay(experienceDay)"
                      >
                        <fa-icon [icon]="icons.edit"></fa-icon>
                        {{ "Edit" | translate }}
                      </button>
                      <button
                        class="btn btn-danger btn-block btn-sm"
                        (click)="
                          deleteExperienceDay(
                            experienceDay.companyId!,
                            experienceDay.id!
                          )
                        "
                      >
                        <fa-icon [icon]="icons.times"></fa-icon>
                        {{ "Delete" | translate }}
                      </button>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        } @else {
          <div>
            <div class="alert alert-info">
              {{ "No results" | translate }}
            </div>
          </div>
        }
      </div>
    </div>
  `,
})
export class ExperienceDaysTableComponent {
  private readonly companyService = inject(CompanyService);
  private readonly modal = inject(ModalService);
  private readonly alertService = inject(AlertService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() experienceDays: ExperienceDay[] = [];
  @Input() company!: Company;
  @Input() loading = false;
  @Output() refresh = new EventEmitter<void>();

  readonly icons = {
    plus: faPlus,
    edit: faEdit,
    times: faTimes,
  };

  getStatusName(status?: ExperienceDayStatus): string {
    switch (status) {
      case ExperienceDayStatus.Concept:
        return "Concept";
      case ExperienceDayStatus.OpenForRegistration:
        return "Open for registration";
      case ExperienceDayStatus.RegistrationClosed:
        return "Registration closed";
      default:
        return "Unknown";
    }
  }

  addExperienceDay(): void {
    const modalOptions = {
      size: "xl",
      scrollable: true,
      backdrop: "static",
    };

    const data = {
      profile: this.company,
      entity: {
        id: "",
        companyId: this.company.id,
        title: "",
        description: "",
        minStudents: 0,
        maxStudents: 0,
        possibleDates: [] as string[],
      },
    };

    this.modal
      .Modal(
        ModalCompanyProfileExperiencedayComponent,
        "",
        "",
        data,
        [],
        modalOptions
      )
      .then((result) => {
        if (result === true) {
          this.refresh.emit();
        }
      });
  }

  editExperienceDay(experienceDay: ExperienceDay): void {
    const modalOptions = {
      size: "xl",
      scrollable: true,
      backdrop: "static",
    };

    const data = {
      profile: this.company,
      entity: experienceDay,
    };

    this.modal
      .Modal(
        ModalCompanyProfileExperiencedayComponent,
        "",
        "",
        data,
        [],
        modalOptions
      )
      .then((result) => {
        if (result === true) {
          this.refresh.emit();
        }
      });
  }

  deleteExperienceDay(companyId: string, id: string): void {
    this.modal
      .Confirm("Delete", "Are you sure you want to delete this?")
      .then((confirm) => {
        if (confirm) {
          this.companyService
            .deleteCompanyExperienceDay({ companyId, id })
            .pipe(
              catchError(() => of(undefined)),
              takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
              next: () => {
                this.alertService.success(alertMessages.successDataRemoved);
                this.refresh.emit();
              },
              error: (err) =>
                this.alertService.errorWithReport(err, 'adminCompany:deleteExperienceDay'),
            });
        }
      })
      .catch(() => {
        // User cancelled confirmation dialog
      });
  }
}
